import {ref, nextTick, watch, type Ref} from 'vue';
import {useRouter} from 'vue-router';
import {usePagedList, type PagedResult, type UsePagedList} from '@/ts/composables/pure/use-paged-list';
import {useIntersectionLoader} from '@/ts/composables/pure/use-intersection-loader';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import {pagesPath} from '@/ts/router/pages-path';

/** What {@link LibraryCollection} exposes; typed here because InstanceType widens to `any`. */
export interface CollectionHandle {
  focusRow(index: number): Promise<void>;
}

/** What a caller must supply to {@link usePagedCollection}. */
export interface UsePagedCollectionOptions<T> {
  fetchPage(page: number): Promise<PagedResult<T>>;
  key(item: T): string;
  onError?(err: unknown, first: boolean): void;
  /** CSS selector for the element that scrolls. See use-intersection-loader for why it matters. */
  rootSelector: string;
  onMissingRoot?(): void;
}

/** The list state plus the glue {@link usePagedCollection} returns. */
export interface UsePagedCollection<T> extends UsePagedList<T> {
  /** Bind to the collection component, so retries can move focus into the rows that arrive. */
  collection: Ref<CollectionHandle | null>;
  /** Pass to the collection component: it hands its sentinel up as it mounts. */
  setSentinel(el: HTMLElement | null): void;
  /** Build the observer. Call once the page is mounted; independent of the sentinel existing. */
  mountObserver(): void;
  /** Watch while there is more to fetch, stop otherwise. `stop` forces the stop. */
  syncObserver(stop?: boolean): void;
  /** Stop watching without tearing the observer down — for a page going off screen. */
  pauseObserver(): void;
  disconnectObserver(): void;
  /** Fetches the first page, then re-arms. */
  loadFirstPage(): Promise<void>;
  /** Refreshes in place, then re-arms. Resolves whether the refresh landed. */
  refreshInPlace(): Promise<boolean>;
  /**
   * Register the page's own first-load routine, so Retry re-runs its prerequisites too.
   *
   * The composable's `loadFirstPage` fetches the list and nothing else, but a page may have to do
   * work around it: the partner page fetches the pack the list is keyed on, the topic page stamps
   * the topic it loaded. Retrying with the list-only version skips that, and the page comes back
   * missing the thing it needed. Defaults to `loadFirstPage` when a page has no such work.
   */
  setFirstPageLoader(load: () => Promise<void>): void;
  retryFirstPage(): void;
  retryMorePages(): void;
}

/**
 * A full-page collection that loads a page at a time: the list state, the sentinel observer, and
 * the glue between them.
 *
 * It exists because that glue is where every bug on this feature has lived — an observer re-armed
 * after a call a guard turned away, a page that stopped watching on the way out and never started
 * again, a licence that dropped while the page sat in the keep-alive cache. Each was fixed once and
 * then reintroduced by the next page that copied the shape without the fixes. A caller now gets
 * them by construction and supplies only what it asks the backend for.
 */
export function usePagedCollection<T>({
  fetchPage,
  key,
  onError,
  rootSelector,
  onMissingRoot,
}: UsePagedCollectionOptions<T>): UsePagedCollection<T> {
  const router = useRouter();
  const list = usePagedList<T>({fetchPage, key, onError});
  const collection = ref<CollectionHandle | null>(null);

  // Assigned below, once loadMore exists; the observer only calls it after mount.
  let onSentinelReached: () => void = () => {};

  const loader = useIntersectionLoader({
    rootSelector,
    rootMargin: '400px',
    onMissingRoot,
    onReach: () => {
      onSentinelReached();
    },
  });

  const syncObserver = (stop = false): void => {
    if (stop || list.allLoaded.value) {
      loader.pause();
      return;
    }
    loader.observe();
  };

  const loadMore = async(): Promise<void> => {
    const ran = await list.loadMore();
    await nextTick();
    // A call a guard turned away must not re-arm: observing queues an immediate callback, which
    // would feed straight back in and spin.
    syncObserver(!ran);
  };

  onSentinelReached = (): void => {
    void loadMore();
  };

  const loadFirstPage = async(): Promise<void> => {
    await list.loadFirst();
    await nextTick();
    syncObserver();
  };

  const refreshInPlace = async(): Promise<boolean> => {
    const refreshed = await list.refresh();
    // Re-armed whatever the outcome: a page coming back from the keep-alive cache paused its
    // observer on the way out, and skipping this leaves the grid unable to page for the visit.
    await nextTick();
    syncObserver();
    return refreshed;
  };

  const focusRowAfterLoad = async(index: number): Promise<void> => {
    await collection.value?.focusRow(index);
  };

  // Defaults to the list-only load; a page with prerequisites replaces it via setFirstPageLoader.
  let firstPageLoader: () => Promise<void> = loadFirstPage;

  const retryFirstPage = (): void => {
    void firstPageLoader().then(async() => focusRowAfterLoad(0));
  };

  const retryMorePages = (): void => {
    const resumeAt = list.items.value.length;
    void list.retryMore().then(async(ran) => {
      await nextTick();
      syncObserver(!ran);
      await focusRowAfterLoad(resumeAt);
    });
  };

  // extractError hands back the bare status code for these, and the rest of the app treats them as
  // a redirect. Without it they land on the generic error card, whose retry re-issues the same
  // failing call for ever.
  watch(list.errorMessage, (message) => {
    if (message === ErrorCodesDto.CONSENT_REQUIRED || message === ErrorCodesDto.CONSENT_NEEDS_REAPPROVAL) {
      void router.push(pagesPath.main.consentRequired);
    } else if (message === ErrorCodesDto.NO_ACTIVE_LICENSES) {
      void router.push({path: pagesPath.account.subscription, query: {reason: ErrorCodesDto.NO_ACTIVE_LICENSES}});
    }
  });

  return {
    ...list,
    collection,
    setSentinel: (el: HTMLElement | null): void => {
      loader.sentinel.value = el;
    },
    setFirstPageLoader: (load: () => Promise<void>): void => {
      firstPageLoader = load;
    },
    mountObserver: loader.create,
    syncObserver,
    pauseObserver: loader.pause,
    disconnectObserver: loader.disconnect,
    loadFirstPage,
    refreshInPlace,
    retryFirstPage,
    retryMorePages,
  };
}
